import { SELECT_OPTION_TYPE } from '../constants/globals';

export enum GetCodeUsage {
  EditDescription = 'edit-description',
}

export enum CodeSets {
  CPT = 'CPT',
  ICD10CM = 'ICD-10-CM',
  SERVICE = 'Service',
  DIAGNOSTIC = 'Diagnostic',
  NDC = 'NDC',
}

export enum BillingCodeUnitType {
  InternationalUnit = 'IU',
  Gram = 'g',
  Milligram = 'mg',
  Milliliter = 'ml',
  Unit = 'U',
}

export const BillingCodeUnitTypeFull = {
  [BillingCodeUnitType.InternationalUnit]: 'International Unit',
  [BillingCodeUnitType.Gram]: 'Gram',
  [BillingCodeUnitType.Milligram]: 'Milligram',
  [BillingCodeUnitType.Milliliter]: 'Milliliter',
  [BillingCodeUnitType.Unit]: 'Unit',
} as const;

export type BillingCodeReference = {
  codeSet: CodeSets;
  code: string;
  clinicID?: number;
  description: string;
};

export type BillingCode = {
  ID: number;
  clinicID: number;
  codeSet: CodeSets;
  unitType: BillingCodeUnitType | null;
  code: string;
  codeShortcut: string;
  description: string;
  billedAmount: number | string;
  variableBilledAmount: boolean;
  defaultModifiers?: string[];
  modifier1: string;
  modifier2: string;
  modifier3: string;
  modifier4: string;
  deleted: boolean;
  payorIds?: number[];
  payors: BillingCodePayor[];
  descriptions?: BillingCodeDescription[];
  nonBillable: boolean;
  isDrugCode: boolean;
  nationalDrugCode: string | null;
};

export type BillingCodePayor = {
  clinicID: number;
  billingCodeID: number;
  payorID: number;
  allowedAmount: number;
  modifier1?: string;
  modifier2?: string;
  modifier3?: string;
  modifier4?: string;
  legalName?: string;
  customCode?: string;
};

export type BillingCodeDescription = {
  id?: number;
  clinicId: number;
  billingCodeId: number;
  description: string;
};

export enum KnownClearingHouses {
  None = 'None',
  TriZetto = 'TriZetto',
  Infinedi = 'Infinedi',
  ClaimMD = 'ClaimMD',
  OfficeAlly = 'Office Ally',
  Waystar = 'Waystar',
  // -- new method below here!
  ApexEDI = 'Apex EDI',
  HNS = 'Health Network Solutions',
  Inovalon = 'Inovalon',
  Ability = 'Ability',
  Availity = 'Availity',
  ASK = 'Administrative Services of Kansas',
  Carisk = 'Carisk',
  Anvicare = 'Anvicare',
  Stedi = 'Stedi',
}

//used by most clearing houses.  def used by trizetto.
export const ELIGIBILITY_270_271_IG_5010 = '005010X279A1';

// These names are used in the EDI
export const KnownClearingHouseNames = {
  [KnownClearingHouses.None]: 'None',
  [KnownClearingHouses.TriZetto]: `TriZetto Provider Solutions`,
  [KnownClearingHouses.Infinedi]: `Infinedi`,
  [KnownClearingHouses.ClaimMD]: `CLAIMMD`,
  [KnownClearingHouses.OfficeAlly]: `Office Ally`,
  [KnownClearingHouses.Waystar]: `Waystar`,
  [KnownClearingHouses.ApexEDI]: `Apex EDI`,
  [KnownClearingHouses.HNS]: `HNS`,
  [KnownClearingHouses.Inovalon]: `Inovalon`,
  [KnownClearingHouses.Ability]: `Ability (an Inovalon Company)`,
  [KnownClearingHouses.Availity]: `Availity`,
  [KnownClearingHouses.ASK]: `ASK`,
  [KnownClearingHouses.Carisk]: `IHCFA`,
  [KnownClearingHouses.Anvicare]: `Anvicare`,
  [KnownClearingHouses.Stedi]: `Stedi`,
};

/**
 * [2025-02-11.0844 by Brian] Trizetto gave us a PDF with a
 * recommended order for the REFs in loop 2300. We've made
 * that the default. Each clearinghouse can override this in
 * the KnownClearingHouseSettings object. [We found at least
 * 3 different orders, so this seemed prudent.]
 */
export const DEFAULT_LOOP_2300_REF_ORDER = ['9F', 'G1', 'F8', 'EA'];

/**
 * Ability is the first clearing house to need special handling
 * of a single field. So, the concept of a function to return it
 * was added. For now, the options is ignored, but we could add
 * anything not just a constant.
 */
export type ClearingHouseSettingsType = {
  isSinglePayorRequired?: boolean;
  useISA06forNM109?: boolean;
  useISA08forNM109?: boolean;
  LOOP_2300_REF_ORDER?: string[];
  isa14?: (options?: any) => string;
  nm109?: (options?: any) => string;
  host?: string;
  path?: string;
  port?: number;
  senderId?: string;
  siteLabel?: string;
  type?: 'edi' | 'manual-edi';
  exchangeVersionCode?: string;
  appReceiverCode?: string;
  receiverId?: string;
  receiverIdQualifier?: string;
  senderIdQualifier?: string;
  initialStep?: 'ready' | 'off';
  name?: string;
  specifics?: string;
  controlNumber?: number;
  step?: string;
  productionSiteId?: string;
  testSiteId?: string;
  default?: number;
  active?: number;
  clinicId?: number;
  production?: number;
  id?: number;
  eligibility?: number;
  integratedClearinghouse?: string;
  /** API-first vendors (integration connectors): default HTTP base URL. */
  apiBaseUrl?: string;
};

const exchangeVersionCode = '005010X222A1';
export const KnownClearingHouseSettings = {
  [KnownClearingHouses.ApexEDI]: {
    useISA06forNM109: false,
    useISA08forNM109: true,
    LOOP_2300_REF_ORDER: DEFAULT_LOOP_2300_REF_ORDER,
    siteLabel: 'Site Tax ID',
    type: 'manual-edi',
    appReceiverCode: 'ApexEDI',
    receiverId: 'ApexEDI',
    receiverIdQualifier: 'ZZ',
    senderIdQualifier: 'ZZ',
    initialStep: 'off',
    exchangeVersionCode,
    eligibility: 0,
  },
  [KnownClearingHouses.Availity]: {
    useISA06forNM109: false,
    useISA08forNM109: true,
    LOOP_2300_REF_ORDER: DEFAULT_LOOP_2300_REF_ORDER,
    siteLabel: 'Customer ID',
    type: 'manual-edi',
    appReceiverCode: '030240928', // GS03
    receiverId: '030240928', // ISA08
    receiverIdQualifier: '01', // ISA07
    senderIdQualifier: 'ZZ', // ISA05
    isa06: 'AV09311993', // ISA06
    initialStep: 'off',
    exchangeVersionCode,
    eligibility: 0,
    padEnd: true,
  },
  [KnownClearingHouses.ASK]: {
    useISA06forNM109: false,
    useISA08forNM109: true,
    LOOP_2300_REF_ORDER: DEFAULT_LOOP_2300_REF_ORDER,
    siteLabel: 'ASK Account ID',
    type: 'manual-edi',
    appReceiverCode: 'BCBSKS',
    receiverId: 'ASK',
    receiverIdQualifier: 'ZZ',
    senderIdQualifier: 'ZZ',
    initialStep: 'off',
    exchangeVersionCode,
    eligibility: 0,
    padEnd: true,
  },
  [KnownClearingHouses.HNS]: {
    useISA06forNM109: false,
    useISA08forNM109: true,
    LOOP_2300_REF_ORDER: DEFAULT_LOOP_2300_REF_ORDER,
    siteLabel: 'Site Tax ID',
    type: 'manual-edi',
    appReceiverCode: '561971088',
    receiverId: '561971088',
    receiverIdQualifier: 'ZZ',
    senderIdQualifier: 'ZZ',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isa14: (options?: any) => {
      return '0';
    },
    initialStep: 'off',
    exchangeVersionCode,
    eligibility: 0,
  },
  [KnownClearingHouses.Infinedi]: {
    host: 'sftp2.infinedi.net',
    port: 22,
    useISA06forNM109: false,
    useISA08forNM109: true,
    LOOP_2300_REF_ORDER: DEFAULT_LOOP_2300_REF_ORDER,
    siteLabel: 'Site Tax ID',
    type: 'manual-edi',
    appReceiverCode: '731572635',
    receiverId: '731572635',
    receiverIdQualifier: '30',
    senderIdQualifier: '30',
    initialStep: 'off',
    exchangeVersionCode,
    eligibility: 0,
  },
  [KnownClearingHouses.TriZetto]: {
    host: 'sftp.gatewayedi.com',
    path: '/',
    port: 22,
    siteLabel: 'Site ID',
    type: 'manual-edi',
    appReceiverCode: '263923727',
    receiverId: '263923727000000',
    receiverIdQualifier: 'ZZ',
    senderIdQualifier: 'ZZ',
    senderId: '321',
    initialStep: 'off',
    exchangeVersionCode,
    eligibility: 0,
  },
  [KnownClearingHouses.OfficeAlly]: {
    siteLabel: 'Site ID',
    type: 'manual-edi',
    appReceiverCode: '330897513',
    receiverId: '330897513',
    receiverIdQualifier: 'ZZ',
    senderIdQualifier: 'ZZ',
    initialStep: 'off',
    exchangeVersionCode,
    eligibility: 0,
  },
  [KnownClearingHouses.ClaimMD]: {
    siteLabel: 'Account Number',
    type: 'manual-edi',
    appReceiverCode: 'CLAIMMD',
    receiverId: 'CLAIMMD',
    receiverIdQualifier: 'ZZ',
    senderIdQualifier: 'ZZ',
    initialStep: 'off',
    exchangeVersionCode,
    eligibility: 0,
  },
  [KnownClearingHouses.Waystar]: {
    siteLabel: 'Account ID',
    type: 'manual-edi',
    appReceiverCode: 'Waystar',
    receiverId: 'Waystar',
    receiverIdQualifier: 'ZZ',
    senderIdQualifier: 'ZZ',
    initialStep: 'off',
    exchangeVersionCode,
    eligibility: 0,
  },
  [KnownClearingHouses.Inovalon]: {
    receiverIdQualifier: 'ZZ',
    senderIdQualifier: 'ZZ',
    useISA06forNM109: false,
    useISA08forNM109: true,
    appReceiverCode: '411973195',
    receiverId: '411973195',
    LOOP_2300_REF_ORDER: DEFAULT_LOOP_2300_REF_ORDER,
  },
  [KnownClearingHouses.Carisk]: {
    receiverIdQualifier: 'ZZ',
    senderIdQualifier: 'ZZ',
    useISA06forNM109: true,
    appReceiverCode: 'IHCFA',
    receiverId: 'IHCFA',
    padEnd: true,
  },
  [KnownClearingHouses.Anvicare]: {
    receiverIdQualifier: 'ZZ',
    senderIdQualifier: 'ZZ',
    useISA06forNM109: true,
    appReceiverCode: 'Anvicare',
    receiverId: 'Anvicare',
  },
  [KnownClearingHouses.Ability]: {
    receiverIdQualifier: 'ZZ',
    senderIdQualifier: 'ZZ',
    appReceiverCode: 'ABILITY837',
    receiverId: 'ABILITY837',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isa14: (options?: any) => {
      return '0';
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    nm109: (options?: any) => {
      return 'ABILITY837';
    },
    LOOP_2300_REF_ORDER: DEFAULT_LOOP_2300_REF_ORDER,
  } as ClearingHouseSettingsType,
  // API-first vendor (integration connector) — no SFTP/X12 envelope settings.
  // API hosts are NOT here: they live in the platform Integration row's
  // `specifics.endpoints` (source of truth, editable without a deploy).
  [KnownClearingHouses.Stedi]: {} as ClearingHouseSettingsType,
} as { [key in KnownClearingHouses]?: ClearingHouseSettingsType };

export const KnownClearingHouseOptions = Object.entries(KnownClearingHouses).reduce((a, c) => {
  a.push({
    text: c[1],
    value: c[1],
  });

  return a;
}, [] as SELECT_OPTION_TYPE[]);

/**
 * Service Facility Location setting on a Billing Profile (12582502320).
 * 'clinicLocation' (default; NULL/undefined mean the same) keeps today's
 * claim behavior; 'billingProfile' suppresses the service facility on
 * claims (837 Loop 2310C, CMS1500 Box 32/32a) because it would be
 * identical to the billing provider.
 */
export type ServiceFacilityLocationSetting = 'clinicLocation' | 'billingProfile';

export type BillingProfile = {
  ID?: number;
  active: 0 | 1;
  type: 'Organization' | 'Individual';
  name: string;
  firstName: string;
  middleInitial?: string;
  lastName: string;
  LastName?: string; // db col name
  suffix?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  extension?: string;
  email?: string;
  contactName?: string;
  clearinghouse?: KnownClearingHouses;
  taxIdType: 'EIN' | 'SSN';
  taxId?: string;
  taxIdDigits?: string; // generated col (numeric-only taxId)
  npi?: number;
  sameAsBillingProviderInfo?: boolean;
  taxonomyCode?: string;
  payToNonPersonEntity?: boolean;
  payToPerson?: boolean;
  serviceFacilityLocation?: ServiceFacilityLocationSetting;
  payToAddressLine1?: string;
  payToAddressLine2?: string;
  payToCity?: string;
  payToState?: string;
  payToZipCode?: string;
  submitterType?: string;
  submitterName?: string;
  submitterFirstName?: string;
  submitterMiddleInitial?: string;
  submitterLastName?: string;
  submitterPhoneNumber?: string;
  submitterExtension?: string;
  submitterEmail?: string;
  submitterContactName?: string;
  providerDesignatedUsers?: string[];
  deleted: number;
  description?: string;
  integrationSiteId?: string;
  integrationPassword?: string;
  credentialsLastCheckedAt?: number | null;
  /** True when clearinghouse matches an active platform Connection API integration. */
  isaConnectionApiVendor?: boolean;
};

export type BillingProfileWithConnectionApiVendor = BillingProfile & {
  ID: number;
  isaConnectionApiVendor: boolean;
};

export const isBillingProfileWithConnectionApiVendor = (
  value: unknown
): value is BillingProfileWithConnectionApiVendor => {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as Partial<BillingProfileWithConnectionApiVendor>;

  return typeof candidate.ID === 'number' && typeof candidate.isaConnectionApiVendor === 'boolean';
};
